import { useState, useRef, useCallback, useEffect } from 'react';
import { capture } from '@/lib/analytics';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function useCloneBuildStream(projectId) {
  const [steps, setSteps] = useState([]);
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewCss, setPreviewCss] = useState(null);
  const [previewJs, setPreviewJs] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const eventSourceRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const isCompleteRef = useRef(false);

  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
      clearInterval(timerRef.current);
    };
  }, []);

  const startBuild = useCallback((briefData) => {
    setSteps([]);
    setThinkingMessages([]);
    setPreviewHtml(null);
    setPreviewCss(null);
    setPreviewJs(null);
    setIsBuilding(true);
    setIsComplete(false);
    isCompleteRef.current = false;
    setError(null);
    setResult(null);
    setElapsedMs(0);

    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startTimeRef.current), 100);

    const token = localStorage.getItem('orbis_token');
    if (!token) {
      setError('Not authenticated');
      setIsBuilding(false);
      clearInterval(timerRef.current);
      return;
    }

    const params = new URLSearchParams({
      token,
      company_name: briefData.company_name,
      industry: briefData.industry || '',
      description: briefData.description || '',
      model: briefData.model || 'xiaomi/mimo-v2-pro',
      premium_mode: briefData.premium_mode ? 'true' : 'false',
      clone_intent: briefData.clone_intent || 'match_visual_style',
      reference_fidelity: String(briefData.reference_fidelity ?? 72),
      conversion_goal: briefData.conversion_goal || '',
      conversion_goal_custom: briefData.conversion_goal_custom || '',
      fallback_mode: briefData.fallback_mode || 'direct-clone',
    });

    capture('clone_build_started', { project_id: projectId, clone_intent: briefData.clone_intent, reference_fidelity: briefData.reference_fidelity });

    const url = `${API_URL}/clone/projects/${projectId}/generate/stream?${params}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('step:start', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => [...prev, {
        id: data.step_id,
        label: data.label,
        description: data.description || '',
        index: data.index,
        totalSteps: data.total_steps,
        status: 'running',
        duration_ms: null,
        startTime: Date.now(),
      }]);
    });

    es.addEventListener('step:complete', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => prev.map(s =>
        s.id === data.step_id ? { ...s, status: 'done', duration_ms: data.duration_ms } : s
      ));
    });

    es.addEventListener('step:error', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => prev.map(s =>
        s.id === data.step_id ? { ...s, status: 'error', errorMsg: data.error } : s
      ));
      capture('build_error', { project_id: projectId, step_id: data.step_id, error: data.error, mode: 'clone' });
      if (!data.fallback) setError(data.error);
    });

    es.addEventListener('thinking', (e) => {
      const data = JSON.parse(e.data);
      setThinkingMessages(prev => [...prev, { stepId: data.step_id, message: data.message, timestamp: Date.now() }]);
    });

    es.addEventListener('preview:update', (e) => {
      const data = JSON.parse(e.data);
      setPreviewHtml(data.html);
      setPreviewCss(data.css || '');
      setPreviewJs(data.js || '');
    });

    es.addEventListener('build:complete', (e) => {
      const data = JSON.parse(e.data);
      capture('clone_build_complete', { project_id: projectId, elapsed_ms: Date.now() - startTimeRef.current });
      isCompleteRef.current = true;
      setResult(data);
      setIsComplete(true);
      setIsBuilding(false);
      clearInterval(timerRef.current);
      es.close();
    });

    es.onerror = () => {
      setTimeout(() => {
        if (isCompleteRef.current) {
          es.close();
          return;
        }
        if (es.readyState === EventSource.CLOSED) {
          setError('Connection lost during build. Check server logs for details.');
          setIsBuilding(false);
          clearInterval(timerRef.current);
          es.close();
        }
      }, 1500);
    };
  }, [projectId]);

  const abortBuild = useCallback(() => {
    eventSourceRef.current?.close();
    clearInterval(timerRef.current);
    setIsBuilding(false);
  }, []);

  return {
    steps, thinkingMessages,
    previewHtml, previewCss, previewJs,
    isBuilding, isComplete, error, result, elapsedMs,
    startBuild, abortBuild,
  };
}
